import { useState, type ReactNode } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { Globe, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import type { WanCandidate } from '@simpleacs/shared';
import { Button, Pill, type PillTone } from '../../components/ui';
import { api } from '../../lib/api';
import { formatUptime } from '../../lib/format';
import type { firewallTitlePill } from '../../lib/wan-apply';
import { runWanDelete, wanDeleteWarning } from '../../lib/wan-delete';
import { wanPortTarget } from '../../lib/wan-ports';
import { useTasks } from '../../lib/tasks';

/**
 * Every connection the router has to the outside world (operator, 2026-10-05:
 * "can we show both hotspot bridge and routed wan as wans or anything that
 * connects the onu to the outside world as a wan", then "split the wans into
 * different cards, not share one").
 *
 * The subscriber WAN keeps the editable WAN card, which names its connection
 * on one line (`WanConnectionLine`). Every other connection — a hotspot
 * bridge, IPTV, voice, the TR-069 channel — is a card of its own below it
 * (`WanConnectionCard`), management last: it is this platform's channel,
 * not the subscriber's.
 */

const ROLE: Record<WanCandidate['role'], { label: string; tone: PillTone; hint: string }> = {
  internet: { label: 'Internet', tone: 'primary', hint: 'Routed by the router: it terminates the connection and serves its LAN.' },
  bridge: {
    label: 'Bridge',
    tone: 'info',
    hint: 'Passed straight through: the router does not terminate it. A hotspot VLAN reaches its SSID this way.',
  },
  iptv: { label: 'IPTV', tone: 'info', hint: 'Carries television.' },
  voip: { label: 'Voice', tone: 'info', hint: 'Carries the phone line.' },
  management: { label: 'Management', tone: 'neutral', hint: "This platform's channel to the router (TR-069)." },
  other: { label: 'Other', tone: 'neutral', hint: 'The router names no service for it.' },
};

const ORDER: WanCandidate['role'][] = ['internet', 'bridge', 'iptv', 'voip', 'other', 'management'];

function modeWord(c: WanCandidate): string | null {
  if (c.role === 'bridge') return null;
  if (c.mode === 'pppoe') return 'PPPoE';
  if (c.mode === 'static') return 'Static';
  if (c.mode === 'dhcp') return 'DHCP';
  return null;
}

/**
 * Each connection on its own card (operator, 2026-10-05: "split the wans into
 * different cards, not share one"). The subscriber WAN keeps the editable WAN
 * card and says which connection it is on one line (`WanConnectionLine`);
 * every OTHER connection — the hotspot bridge, IPTV, the TR-069 channel —
 * gets a card of its own below it, built here, in the order the list used.
 * Their settings are changed in "All settings", which holds every leaf; these
 * cards read.
 */
export function otherWanConnections(candidates: WanCandidate[]): WanCandidate[] {
  return [...candidates].filter((c) => !c.primary).sort((a, b) => ORDER.indexOf(a.role) - ORDER.indexOf(b.role));
}

/**
 * The bar a connection's card wears, collapsed or open: what it is, whether it
 * is up, and — on a connection the router terminates (internet, management) —
 * its firewall (operator, 2026-10-09: "show firewall pill too for routed wan
 * and mngt, remove those highlighted text"). The connection's name and VLAN
 * are no longer on the bar; they are the card's first rows. A bridge gets no
 * firewall pill: the router passes it straight through.
 */
/**
 * The router's firewall as a shield alone — green on, red off — with the words
 * in the tooltip and for screen readers (operator, 2026-10-28: "just a pill
 * with the shield icon, green for on, red for off").
 */
export function FirewallPill({ pill }: { pill: NonNullable<ReturnType<typeof firewallTitlePill>> }) {
  return (
    <Pill state tone={pill.tone} title={pill.title}>
      {pill.on ? <ShieldCheck className="size-3.5" aria-hidden /> : <ShieldOff className="size-3.5" aria-hidden />}
      <span className="sr-only">{pill.label}</span>
    </Pill>
  );
}

/**
 * Which connections carry a session pill. A bridge has no session of its own
 * and the management connection is the platform's own channel, so neither
 * wears "Connected" / "Passing through" (operator, 2026-10-28); an internet or
 * other connection still says whether it is up.
 */
export function showsSessionPill(c: WanCandidate): boolean {
  return c.role !== 'bridge' && c.role !== 'management';
}

export function wanConnectionTitle(c: WanCandidate, firewall?: ReturnType<typeof firewallTitlePill>) {
  const role = ROLE[c.role];
  // The firewall is one setting for the whole router, so it is said once, on the
  // Internet card; the management card carries no shield (operator, 2026-10-28:
  // "remove the shield from the mngt card, leave the firewall values").
  const terminated = c.role === 'internet';
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Globe className="size-4 text-muted" />
      WAN
      <Pill tone={role.tone} title={role.hint}>
        {role.label}
      </Pill>
      {showsSessionPill(c) && (
        <Pill state tone={c.connected ? 'success' : 'warn'} dot>
          {c.connected ? 'Connected' : 'Not connected'}
        </Pill>
      )}
      {terminated && firewall && <FirewallPill pill={firewall} />}
    </span>
  );
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] items-baseline gap-3 px-5 py-2.5">
      <span className="text-sm text-muted">{label}</span>
      <span className={mono ? 'font-mono text-xs text-ink' : 'text-sm text-ink'}>
        {value ?? <span className="text-faint">—</span>}
      </span>
    </div>
  );
}

/**
 * One connection's card body: what the router reports about it, and what can be changed on it. The
 * management connection has its own card with the internet card's controls (`ManagementWanCard`).
 */
export function WanConnectionCard({
  connection: c,
  ports,
  deleteAction,
}: {
  connection: WanCandidate;
  /**
   * The ports that ride this connection, chosen here (`WanPorts`) — the same
   * records as the Ports tab (2026-10-14: one concept of a bridge; the
   * WiFi-only Detach / Delete WiFi and VLAN buttons are gone).
   */
  ports?: ReactNode;
  /** The connection's Delete (`WanDeleteButton`) — never on the management connection. */
  deleteAction?: ReactNode;
}) {
  const role = ROLE[c.role];
  const bridged = c.role === 'bridge';
  return (
    <div className="divide-y divide-line">
      <p className="px-5 py-3 text-sm text-muted">{role.hint}</p>
      <Row label="Name" value={c.label} />
      <Row label="Connection type" value={c.connectionType} />
      <Row label="VLAN" value={c.vlanId} />
      {!bridged && <Row label="Addressing" value={modeWord(c)} />}
      {showsSessionPill(c) && (
        <Row
          label="Status"
          value={
            <Pill state tone={c.connected ? 'success' : 'warn'} dot>
              {c.connected ? 'Connected' : 'Not connected'}
            </Pill>
          }
        />
      )}
      {!bridged && (
        <>
          <Row label="IP address" value={c.externalIp} mono />
          <Row label="Subnet mask" value={c.subnetMask ?? null} mono />
          <Row label={c.kind === 'ppp' ? 'Peer' : 'Gateway'} value={c.gateway ?? null} mono />
          <Row label="DNS" value={c.dns ?? null} mono />
          <Row label="NAT" value={c.natEnabled === null || c.natEnabled === undefined ? null : c.natEnabled ? 'On' : 'Off'} />
        </>
      )}
      <Row label="WAN MAC" value={c.macAddress ?? null} mono />
      {c.connected && c.uptimeSeconds !== null && c.uptimeSeconds > 0 && (
        <Row label="Uptime" value={formatUptime(c.uptimeSeconds)} />
      )}
      {ports && wanPortTarget(c) ? (
        ports
      ) : (
        <Row
          label="Serves"
          value={c.serves.length > 0 ? c.serves.join(', ') : <span className="text-faint">no socket or SSID bound</span>}
        />
      )}
      {deleteAction}
      <p className="px-5 py-3 text-xs text-faint">
        Every setting of this connection is in <span className="text-muted">All settings</span>, under WAN
        connections.
      </p>
    </div>
  );
}

/** Whether a WAN interface may be deleted from its card — anything but the management connection, a bridge included (`lib/wan-delete.ts`). */
export { wanDeletable } from '../../lib/wan-delete';

/**
 * Delete one WAN interface, like a WiFi network (operator, 2026-10-07: "make
 * wan interface deletable like wifi interfaces"): two deliberate presses with
 * the consequence said in between, the work in the tasks tray. The router's
 * own connection goes over TR-069; the OLT's own comes off the chassis first
 * (the ONU route does that). The record then says there is no WAN on that
 * VLAN, so nothing builds it back until the WAN is applied again. A bridge
 * too (2026-10-14): whatever WiFi rides it is detached first, and the LAN
 * sockets it held are released (`runWanDelete`).
 */
export function WanDeleteButton({
  connection: c,
  endpoint,
  deviceId,
  serial,
  href,
  invalidate,
}: {
  connection: WanCandidate;
  /** The DELETE route, without the query: the ONU's (`/olts/:id/onus/:onuId/wan`) or the router's (`/devices/:id/wan`). */
  endpoint: string;
  /** The router, for detaching the WiFi a bridge carries before the bridge goes. */
  deviceId: string | null;
  serial: string;
  href?: string | undefined;
  invalidate: QueryKey[];
}) {
  const tasks = useTasks();
  const [confirming, setConfirming] = useState(false);
  const label = c.label || 'this WAN';
  const start = () => {
    setConfirming(false);
    tasks.start<{ message: string }>({
      label: `Delete WAN ${label} — ${serial}`,
      ...(href ? { href } : {}),
      run: () => runWanDelete(c, { delete: endpoint, deviceId }, api),
      done: (r) => r.message,
      invalidate,
    });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-5 py-3">
      {confirming ? (
        <>
          <span className="text-sm text-danger">{wanDeleteWarning(c)}</span>
          <Button size="sm" variant="danger" onClick={start}>
            Delete WAN
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="text-danger"
          icon={<Trash2 className="size-3.5" />}
          onClick={() => setConfirming(true)}
        >
          Delete WAN
        </Button>
      )}
    </div>
  );
}

/** The subscriber WAN's own card, on one line: which connection the fields below edit. */
export function WanConnectionLine({ connection: c }: { connection: WanCandidate }) {
  const role = ROLE[c.role];
  return (
    <div className="grid grid-cols-[9rem_1fr] items-baseline gap-3 px-5 py-2.5">
      <span className="text-sm text-muted">Connection</span>
      <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-ink">
        <Pill tone={role.tone} title={role.hint}>
          {role.label}
        </Pill>
        <span className="font-medium">{c.label}</span>
        {/* A bridge has no session of its own, so it wears no session pill at all
            (operator, 2026-10-28) — "Not connected" read as a fault on a bridge whose
            status leaves had simply not been read yet (HS MC6 - Maggie3, 2026-10-14). */}
        {showsSessionPill(c) && (
          <Pill state tone={c.connected ? 'success' : 'warn'} dot>
            {c.connected ? 'Connected' : 'Not connected'}
          </Pill>
        )}
        {c.serves.length > 0 && <span className="text-xs text-muted">serves {c.serves.join(', ')}</span>}
      </span>
    </div>
  );
}
